import { isPlatformBrowser } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Injector,
    Input,
    OnChanges,
    Output,
    PLATFORM_ID,
    QueryList,
    SimpleChanges,
    ViewChildren,
    afterNextRender,
    inject,
} from '@angular/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { IFilterValue, ISortingOption } from '../../_shared/models';
import { ButtonComponent } from '../../button/button.component';
import { IconComponent } from '../../icon/icon.component';
import { SearchComponent } from '../../search/search.component';

// TODO: Move interface to lib/_shared/types/
export interface IFilterItem {
    name: string;
    active: boolean;
    queried: boolean;
}

@Component({
    selector: 'sirius-filter-dropdown',
    templateUrl: './filter-dropdown.component.html',
    styleUrls: ['./filter-dropdown.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'sir-text-style-paragraph-m-default' },
    imports: [SearchComponent, ButtonComponent, IconComponent, TranslateModule]
})
export class FilterDropdownComponent implements OnChanges {
    @Input() values: IFilterValue[] | ISortingOption[] = [];
    @Input() multiSelect = true;
    /** Id of the trigger; listbox `aria-labelledby` points here. */
    @Input() filterLabelId?: string | null;

    @Output() selectionChange = new EventEmitter<IFilterValue[]>();
    @Output() activatedFilters = new EventEmitter<IFilterValue[]>();
    @Output() closeDropdown = new EventEmitter<void>();

    hasActiveFilters = false;
    showSearch = false;
    activeOptionIndex = 0;
    readonly control = new FormControl('', { nonNullable: true });

    @ViewChildren('option') optionElements?: QueryList<ElementRef<HTMLLIElement>>;

    private readonly _items = new Map<string, IFilterItem>();
    private readonly _cdr = inject(ChangeDetectorRef);
    private readonly _injector = inject(Injector);
    private readonly _platformId = inject(PLATFORM_ID);

    constructor() {
        this.control.valueChanges.pipe(takeUntilDestroyed()).subscribe((query) => {
            const q = query ?? '';
            for (const id of this._items.keys()) {
                this.setQueried(id, q);
            }
            this.syncActiveOptionIndex();
            this._cdr.detectChanges();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['values']) {
            this.syncFromValues();
        }
    }

    getName(id: string): string {
        return this._items.get(id)?.name ?? '';
    }

    getQueriedFilterIds(): string[] {
        const q = this.control.value;
        if (!q) return [...this._items.keys()];
        return [...this._items.keys()].filter((id) => this._items.get(id)?.queried);
    }

    toggleActive(id: string): void {
        if (!this.multiSelect) {
            this._items.forEach((_, key) => this.setActive(key, false));
            this.setActive(id, true);
            this.saveSelection();
            return;
        }
        this.setActive(id, !this.isActive(id));
        this.refreshSelectionMeta();
    }

    isActive(id: string): boolean {
        return this._items.get(id)?.active ?? false;
    }

    saveSelection(): void {
        let hasChanges = false;
        const activated: IFilterValue[] = [];

        for (const v of this.values) {
            const item = this._items.get(v.id);
            if (!item || v.active === item.active) continue;
            hasChanges = true;
            if (item.active) {
                activated.push({ id: v.id, name: item.name, active: item.active });
            }
            v.active = item.active;
        }

        if (hasChanges) {
            if (activated.length) this.activatedFilters.emit(activated);
            this.selectionChange.emit(this.values as IFilterValue[]);
        }
        this.closeDropdown.emit();
    }

    clearSelection(): void {
        this._items.forEach((_, id) => this.setActive(id, false));
        this.saveSelection();
    }

    onOptionKeydown(event: KeyboardEvent, index: number): void {
        const ids = this.getQueriedFilterIds();
        const n = ids.length;
        if (!n) return;

        const key = event.key;
        if (key === 'ArrowDown') {
            event.preventDefault();
            this.focusOption((index + 1) % n);
        } else if (key === 'ArrowUp') {
            event.preventDefault();
            this.focusOption((index - 1 + n) % n);
        } else if (key === 'Home') {
            event.preventDefault();
            this.focusOption(0);
        } else if (key === 'End') {
            event.preventDefault();
            this.focusOption(n - 1);
        } else if (key === 'Enter' || key === ' ' || key === 'Spacebar') {
            event.preventDefault();
            this.toggleActive(ids[index]);
        } else if (key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            this.closeDropdown.emit();
        }
    }

    focusFirstActiveOrFirstOption(): void {
        const ids = this.getQueriedFilterIds();
        if (!ids.length) return;
        const i = ids.findIndex((id) => this.isActive(id));
        this.focusAfterRender(i >= 0 ? i : 0);
    }

    focusLastOption(): void {
        const ids = this.getQueriedFilterIds();
        if (!ids.length) return;
        this.focusAfterRender(ids.length - 1);
    }

    private syncFromValues(): void {
        this._items.clear();
        for (const v of this.values) {
            this._items.set(v.id, { name: v.name, active: v.active, queried: false });
        }
        const q = this.control.value;
        if (q) {
            for (const id of this._items.keys()) this.setQueried(id, q);
        }
        this.showSearch = this.values.length >= 15;
        this.refreshSelectionMeta();
    }

    private setActive(id: string, active: boolean): void {
        const item = this._items.get(id);
        if (item) item.active = active;
    }

    private setQueried(id: string, query: string): void {
        const item = this._items.get(id);
        if (item) item.queried = item.name.toLowerCase().includes(query.toLowerCase());
    }

    private refreshSelectionMeta(): void {
        this.hasActiveFilters = [...this._items.values()].some((i) => i.active);
        this.syncActiveOptionIndex();
    }

    private focusOption(i: number): void {
        this.activeOptionIndex = i;
        if (!isPlatformBrowser(this._platformId)) return;
        this.optionElements?.get(i)?.nativeElement.focus();
    }

    private focusAfterRender(i: number): void {
        this.activeOptionIndex = i;
        if (!isPlatformBrowser(this._platformId)) return;
        afterNextRender(() => this.optionElements?.get(i)?.nativeElement.focus(), { injector: this._injector });
    }

    private syncActiveOptionIndex(): void {
        const ids = this.getQueriedFilterIds();
        if (!ids.length) {
            this.activeOptionIndex = 0;
            return;
        }
        const activeIdx = ids.findIndex((id) => this.isActive(id));
        this.activeOptionIndex = activeIdx >= 0 ? activeIdx : Math.min(this.activeOptionIndex, ids.length - 1);
    }
}
